// src/game-system/skills/skillSystem.js
import { SKILL_RANKS } from './skillProgression';
import { calculateSkillModifier, getAbilityModifier } from '../character/calculations';

/**
 * @typedef {Object} Skill
 * @property {string} name - Skill name
 * @property {string} attribute - Associated attribute (strength, dexterity, etc.)
 * @property {string} description - Skill description
 * @property {boolean} trainedOnly - Whether it requires training
 * @property {string[]} synergies - Related skills that provide bonuses
 * @property {string[]} usedFor - Common uses of this skill
 * @property {string} category - Skill category (physical, mental, social, etc.)
 */

/**
 * @type {Object.<string, Skill>}
 */
export const SKILLS = {
  // Strength-based skills
  athletics: {
    name: "Athletics",
    attribute: "strength",
    description: "Your training in physical activities involving strength, endurance, and agility",
    trainedOnly: false,
    synergies: ["Acrobatics"],
    usedFor: ["Climbing", "Jumping", "Swimming", "Lifting heavy objects"],
    category: "physical"
  },

  // Dexterity-based skills
  acrobatics: {
    name: "Acrobatics",
    attribute: "dexterity",
    description: "Your ability to perform agile movements, maintain balance, and perform stunts",
    trainedOnly: false,
    synergies: ["Athletics"],
    usedFor: ["Balance", "Tumbling", "Escaping bonds", "Gymnastic feats"],
    category: "physical"
  },
  sleightOfHand: {
    name: "Sleight of Hand",
    attribute: "dexterity",
    description: "Your ability to perform manual trickery and misdirection",
    trainedOnly: true,
    synergies: ["Deception"],
    usedFor: ["Pickpocketing", "Hiding small objects", "Legerdemain"],
    category: "physical"
  },
  stealth: {
    name: "Stealth",
    attribute: "dexterity",
    description: "Your ability to hide, move silently, and avoid detection",
    trainedOnly: false,
    synergies: ["Perception"],
    usedFor: ["Hiding", "Moving quietly", "Shadowing someone"],
    category: "physical"
  },

  // Constitution-based skills
  endurance: {
    name: "Endurance",
    attribute: "constitution",
    description: "Your ability to withstand physical stress and hardship",
    trainedOnly: false,
    synergies: ["Athletics"],
    usedFor: ["Resisting exhaustion", "Surviving harsh environments", "Holding breath"],
    category: "physical"
  },

  // Intelligence-based skills
  arcana: {
    name: "Arcana",
    attribute: "intelligence",
    description: "Your knowledge of magic, magical items, and arcane symbols",
    trainedOnly: true,
    synergies: ["History", "Religion"],
    usedFor: ["Identifying spells", "Recalling magical lore", "Understanding arcane symbols"],
    category: "knowledge"
  },
  history: {
    name: "History",
    attribute: "intelligence",
    description: "Your knowledge of past events, people, and cultures",
    trainedOnly: false,
    synergies: ["Arcana", "Religion"],
    usedFor: ["Recalling historical events", "Identifying ancient cultures", "Recognizing historical figures"],
    category: "knowledge"
  },
  investigation: {
    name: "Investigation",
    attribute: "intelligence",
    description: "Your ability to search for and analyze clues",
    trainedOnly: false,
    synergies: ["Perception"],
    usedFor: ["Searching for clues", "Deductive reasoning", "Finding hidden mechanisms"],
    category: "knowledge"
  },
  nature: {
    name: "Nature",
    attribute: "intelligence",
    description: "Your knowledge of the natural world, plants, and animals",
    trainedOnly: false,
    synergies: ["Survival"],
    usedFor: ["Identifying plants and animals", "Weather forecasting", "Natural phenomena"],
    category: "knowledge"
  },

  // Wisdom-based skills
  animalHandling: {
    name: "Animal Handling",
    attribute: "wisdom",
    description: "Your ability to calm, train, and control animals",
    trainedOnly: false,
    synergies: ["Nature"],
    usedFor: ["Calming animals", "Riding mounts", "Training creatures"],
    category: "social"
  },
  insight: {
    name: "Insight",
    attribute: "wisdom",
    description: "Your ability to determine someone's intentions and read body language",
    trainedOnly: false,
    synergies: ["Perception"],
    usedFor: ["Detecting lies", "Reading intentions", "Understanding motives"],
    category: "social"
  },
  medicine: {
    name: "Medicine",
    attribute: "wisdom",
    description: "Your knowledge of treating injuries and diagnosing illness",
    trainedOnly: true,
    synergies: ["Nature"],
    usedFor: ["Stabilizing dying creatures", "Diagnosing illnesses", "Treating wounds"],
    category: "knowledge"
  },
  perception: {
    name: "Perception",
    attribute: "wisdom",
    description: "Your awareness of your surroundings and ability to notice details",
    trainedOnly: false,
    synergies: ["Investigation"],
    usedFor: ["Spotting hidden objects", "Hearing distant sounds", "Noticing details"],
    category: "awareness"
  },
  survival: {
    name: "Survival",
    attribute: "wisdom",
    description: "Your ability to navigate, track, and find food in the wilderness",
    trainedOnly: false,
    synergies: ["Nature"],
    usedFor: ["Tracking creatures", "Foraging for food", "Navigating wilderness", "Weather prediction"],
    category: "physical"
  },

  // Charisma-based skills
  deception: {
    name: "Deception",
    attribute: "charisma",
    description: "Your ability to convincingly hide the truth or tell lies",
    trainedOnly: false,
    synergies: ["Persuasion"],
    usedFor: ["Telling convincing lies", "Creating disguises", "Concealing intentions"],
    category: "social"
  },
  intimidation: {
    name: "Intimidation",
    attribute: "charisma",
    description: "Your ability to influence others through threats or displays of power",
    trainedOnly: false,
    synergies: ["Persuasion"],
    usedFor: ["Coercing information", "Frightening others", "Shows of force"],
    category: "social"
  },
  performance: {
    name: "Performance",
    attribute: "charisma",
    description: "Your ability to entertain an audience through music, dance, or other arts",
    trainedOnly: false,
    synergies: ["Persuasion"],
    usedFor: ["Playing music", "Acting", "Public speaking", "Dancing"],
    category: "social"
  },
  persuasion: {
    name: "Persuasion",
    attribute: "charisma",
    description: "Your ability to influence others through logic, charm, or good will",
    trainedOnly: false,
    synergies: ["Deception", "Insight"],
    usedFor: ["Changing opinions", "Negotiating", "Making friends"],
    category: "social"
  }
};

// Difficulty class scale for skill checks
export const DIFFICULTY_CLASS = {
  VERY_EASY: 5,
  EASY: 10,
  MEDIUM: 15,
  HARD: 20,
  VERY_HARD: 25,
  NEARLY_IMPOSSIBLE: 30
};

/**
 * Skill check system
 */
export class SkillSystem {
  /**
   * Roll a d20
   * @returns {number} Random number between 1 and 20
   */
  static rollD20() {
    return Math.floor(Math.random() * 20) + 1;
  }

  /**
   * Perform a skill check
   * @param {Object} character - Character performing the check
   * @param {string} skillName - Name of the skill
   * @param {number} difficulty - Difficulty class of the check
   * @param {Object} [options] - Additional options (advantage, disadvantage, etc)
   * @returns {Object} Result of the skill check
   */
  static performCheck(character, skillName, difficulty, options = {}) {
    // Get the skill information
    const skill = SKILLS[skillName.toLowerCase()];
    if (!skill) {
      throw new Error(`Skill '${skillName}' not found`);
    }

    // Handle trained-only skills
    if (skill.trainedOnly) {
      const characterSkill = character.skills?.find(s =>
        s.skill.name.toLowerCase() === skillName.toLowerCase()
      );
      if (!characterSkill || characterSkill.level < 1) {
        return {
          success: false,
          criticalSuccess: false,
          criticalFailure: false,
          roll: 0,
          total: 0,
          difficulty,
          details: {
            baseRoll: 0,
            skillModifier: 0,
            situationalModifiers: [],
            message: `You are not trained in ${skill.name}`
          }
        };
      }
    }

    // Check if character has advantage or disadvantage
    const hasAdvantage = options.advantage || false;
    const hasDisadvantage = options.disadvantage || false;

    // Roll the dice (twice if advantage/disadvantage)
    let roll1 = this.rollD20();
    let roll2 = this.rollD20();
    let finalRoll;

    // Determine which roll to use
    if (hasAdvantage && !hasDisadvantage) {
      finalRoll = Math.max(roll1, roll2);
    } else if (hasDisadvantage && !hasAdvantage) {
      finalRoll = Math.min(roll1, roll2);
    } else {
      finalRoll = roll1;
    }

    // Calculate skill modifier
    const skillModifier = calculateSkillModifier(character, skillName);

    // Apply any situational modifiers
    const situationalModifiers = options.modifiers || [];
    const totalSituationalModifier = situationalModifiers.reduce((sum, mod) => sum + mod.value, 0);

    // Calculate total
    const total = finalRoll + skillModifier + totalSituationalModifier;

    // Determine success or failure
    const success = total >= difficulty;
    const criticalSuccess = finalRoll === 20;
    const criticalFailure = finalRoll === 1;

    // Generate outcome message
    let message = '';
    if (criticalSuccess) {
      message = 'Critical success! You perform the task with exceptional skill.';
    } else if (criticalFailure) {
      message = 'Critical failure! Your attempt goes horribly wrong.';
    } else if (success) {
      message = 'Success! You complete the task successfully.';
    } else {
      message = 'Failure. You are unable to complete the task.';
    }

    // Create detailed result object
    return {
      success: criticalSuccess || (success && !criticalFailure),
      criticalSuccess,
      criticalFailure,
      roll: finalRoll,
      total,
      difficulty,
      details: {
        baseRoll: finalRoll,
        secondRoll: (hasAdvantage || hasDisadvantage) ? roll2 : null,
        advantage: hasAdvantage && !hasDisadvantage,
        disadvantage: hasDisadvantage && !hasAdvantage,
        skillModifier,
        situationalModifiers,
        message
      }
    };
  }

  /**
   * Get skills for a specific attribute
   * @param {string} attribute - Attribute name
   * @returns {Skill[]} Array of skills for that attribute
   */
  static getSkillsByAttribute(attribute) {
    return Object.values(SKILLS).filter(skill => skill.attribute === attribute);
  }

  /**
   * Get skills by category
   * @param {string} category - Skill category
   * @returns {Skill[]} Array of skills in that category
   */
  static getSkillsByCategory(category) {
    return Object.values(SKILLS).filter(skill => skill.category === category);
  }

  /**
   * Determine the DC of a task based on its difficulty
   * @param {string} difficultyLevel - Verbal description of difficulty
   * @returns {number} DC value
   */
  static getDifficultyClass(difficultyLevel) {
    return DIFFICULTY_CLASS[difficultyLevel.toUpperCase()] || DIFFICULTY_CLASS.MEDIUM;
  }

  /**
   * Gain experience in a skill
   * @param {Object} character - Character data
   * @param {string} skillName - Name of the skill to gain XP in
   * @param {number} amount - Amount of XP to gain
   * @returns {Object} Updated character data
   */
  static gainSkillExperience(character, skillName, amount) {
    if (!character || !character.skills) return character;

    const updatedSkills = [...character.skills];
    const skillIndex = updatedSkills.findIndex(s =>
      s.skill.name.toLowerCase() === skillName.toLowerCase()
    );

    if (skillIndex === -1) {
      // Character doesn't have this skill yet, add it
      const newSkill = {
        skill: {
          name: skillName,
          ...SKILLS[skillName.toLowerCase()]
        },
        level: 0,
        experience: amount
      };
      updatedSkills.push(newSkill);
    } else {
      // Update existing skill
      const skill = updatedSkills[skillIndex];
      skill.experience += amount;

      // Check for level up
      const currentRank = skill.level;
      const nextRankXP = this.getExperienceForNextRank(currentRank);

      if (nextRankXP > 0 && skill.experience >= nextRankXP) {
        skill.level += 1;
      }

      updatedSkills[skillIndex] = skill;
    }

    return {
      ...character,
      skills: updatedSkills
    };
  }

  /**
   * Get required XP for next skill rank
   * @param {number} currentRank - Current skill rank
   * @returns {number} XP required for next rank (-1 if max rank)
   */
  static getExperienceForNextRank(currentRank) {
    const maxRank = Object.keys(SKILL_RANKS).length - 1;
    if (currentRank >= maxRank) return -1; // Already at max rank

    const nextRank = currentRank + 1;
    return nextRank * nextRank * 100; // Simple progression: 100, 400, 900, 1600
  }
}